/**
 * One document: its viewer, its metadata, its version history, its actions.
 *
 * Three rules the layout enforces, each because the alternative is a real
 * failure:
 *
 * 1. **The current release is marked, everywhere.** A version list where the
 *    approved release is not obvious invites somebody to read a superseded
 *    revision and act on it.
 * 2. **AI-suggested metadata looks different from confirmed metadata** and
 *    carries a Confirm control. Extraction proposes; a person confirms. A
 *    retention date nobody checked is fiction, and it must not look like fact.
 * 3. **A masked sensitive value never shows a ciphertext.** The server returns
 *    a mask for a reader outside `reveal_roles`; showing the envelope instead
 *    would hand out something to work on offline.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { DocumentReview } from './DocumentReview'
import { SecureDocViewer } from './SecureDocViewer'
import {
  actionLabel, formatBytes, formatWhen, isReturningAction, metadataFailures,
  orderFolders,
  READ_ONLY_EXPLANATION,
  humaniseState, STATE_CHIP_TONE, STATE_LABELS, STATE_MEANING,
  type DmsAction, type DmsApprovalDetail, type DmsClient, type DmsDocumentDetail,
  type DmsDocumentSource,
  type DmsField, type DmsFolder, type DmsMetadataValue, type DmsPendingApproval,
  type DmsSignatureStatus, type DmsUser, type DmsValidationFailure,
  type DmsVersion, type DmsWhereUsedRow,
} from './contracts'
import { personName } from './people'
import { DmsApprovalReview } from './DmsApprovalReview'
import { Icon } from './icons'
import { FactChip } from './ui'
import {
  btnDangerStyle, btnPrimaryStyle, btnStyle as sharedBtnStyle, explainStyle,
  inputStyle as sharedInputStyle, labelStyle, RADIUS, T, TYPE,
} from './styles'
import { StatusChip } from './ui'
import {
  generatedOnFiling, isProtectedField, MetadataFieldInput, MetadataValue,
} from './MetadataFieldInput'
import { CompoundPanel } from './CompoundPanel'
import { SignaturePanel } from './SignaturePanel'
import { WhereUsedPanel } from './WhereUsedPanel'

export interface DocumentDetailProps {
  client: DmsClient
  documentId: string
  onClose?: () => void
  onChanged?: () => void
  /** Current user key, so "checked out by you" reads correctly. */
  userKey?: string
  /** The directory, so every screen names people rather than showing keys. */
  users?: DmsUser[]
  /** The folder tree, so the document can say where it is filed and be
   *  re-filed from here without going back to the list. */
  folders?: DmsFolder[]
  /** Called after an approval is decided here, so the host can refresh its
   *  own count of what is still waiting. */
  onDecided?: () => void
  /**
   * Opens the authoring sub-module on this document. Optional — a host that
   * does not serve `/authoring/*` simply never offers to edit or compare, and
   * the version list reads exactly as it did before authoring existed.
   */
  onAuthor?: (mode:
    | { kind: 'open'; documentId: string; versionId?: string }
    | { kind: 'compare'; documentId: string; versionA: string; versionB: string }
  ) => void
  /**
   * Open the RECORD a Document Form filed this document from.
   *
   * Editing such a document in the repository's own writer would fork it away
   * from the transaction behind it — the wording would change and the data
   * would not — so where a source exists, Edit leads here instead. A host that
   * cannot navigate to a transaction omits it and the ordinary editor is
   * offered, which is what shipped before.
   */
  onOpenSource?: (source: DmsDocumentSource) => void
  /**
   * Whether this caller may re-index (the host's administrator probe said
   * yes). The server refuses regardless; this only decides whether the
   * button renders.
   */
  canReindex?: boolean
  /** Citation target: the page + passage a Q&A answer cited, so the viewer
   *  opens there rather than at page one. */
  focus?: { page?: number | null; excerpt?: string } | null
}

export function DocumentDetail({
  client, documentId, onClose, onChanged, userKey, users: givenUsers, folders,
  onAuthor, onOpenSource, canReindex, focus, onDecided,
}: DocumentDetailProps) {
  const [detail, setDetail] = useState<DmsDocumentDetail | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [selectedVersion, setSelectedVersion] = useState<string>('')
  /**
   * The record this document is the face of, when a Document Form filed it.
   *
   * `undefined` while unknown, `null` once the server has said there is none —
   * the two must stay distinct or the Edit button flips from the ordinary
   * editor to the form (or back) a moment after the panel draws.
   */
  const [source, setSource] = useState<DmsDocumentSource | null | undefined>(
    undefined)
  /** Whether the reader has asked for the whole history. */
  const [allVersions, setAllVersions] = useState(false)
  const [edits, setEdits] = useState<Record<string, string>>({})
  // Fields an on-change rule has LOCKED (1) or explicitly unlocked (0). Runtime
  // only — it never touches the stored definition, and it is cleared with the
  // document, because a lock is a consequence of the values in front of you.
  const [ruleProtect, setRuleProtect] = useState<Record<string, 0 | 1>>({})
  // Which box a refused save named, so the reader is pointed at the field
  // rather than at one flattened sentence above a dozen inputs. Cleared with
  // the document and on the next edit of the field it belongs to.
  const [fieldErrors, setFieldErrors] =
    useState<Record<string, DmsValidationFailure[]>>({})
  // What a 'W'/'P' rule said about a save that went through anyway.
  const [savedAdvisories, setSavedAdvisories] = useState<string[]>([])
  // Both are OPTIONAL capabilities: a host that does not serve them leaves
  // these null and the panels render nothing, with no branching here.
  const [signatures, setSignatures] = useState<DmsSignatureStatus | null>(null)
  const [whereUsed, setWhereUsed] = useState<
    { parents: DmsWhereUsedRow[]; hidden_parents: number } | null>(null)

  const load = useCallback(async () => {
    setError('')
    try {
      const next = await client.detail(documentId)
      setDetail(next)
      setSelectedVersion(String(next.document.current_version_id || ''))
      setEdits({})
      setRuleProtect({})
      setFieldErrors({})
    } catch (loadError) {
      setDetail(null)
      setError(loadError instanceof Error ? loadError.message
        : 'This document could not be opened.')
    }
  }, [client, documentId])

  /**
   * A metadata value changed: record it, then run the field's on-change rule.
   *
   * The rule is the transaction designer's `item_change`, run by the same
   * engine code, so what comes back is the same three things: values to set on
   * OTHER fields, fields to lock or unlock, and a non-blocking message. Its
   * updates land in `edits` beside the user's own, which is what makes them
   * part of the same save — a rule that filled three fields must not leave the
   * document in a state where only the typed one was kept.
   *
   * The trigger's own value is never echoed back: a rule that returned it
   * would fight the person still typing.
   */
  const editMetadata = useCallback((key: string, value: string) => {
    setEdits((prior) => ({ ...prior, [key]: value }))
    setFieldErrors((prior) => (prior[key] ? { ...prior, [key]: [] } : prior))
    const doctype = detail?.doctype
    const field = (doctype?.fields || []).find((f) => f.field_key === key)
    if (!doctype || !field?.has_item_change || !client.metadataItemChange) return
    // Built from the values as they are right now, with this edit applied —
    // the rule reads the whole record, not just what changed.
    const stored: Record<string, unknown> = {}
    for (const row of detail?.metadata || []) {
      if (row.value != null) stored[String(row.field_key)] = row.value
    }
    const values = { ...stored, ...edits, [key]: value }
    void client.metadataItemChange({
      doctypeId: String(doctype.id), fieldKey: key, values,
    }).then((result) => {
      if (result?.error) { setError(result.error); return }
      const updates = result?.updates || {}
      const next: Record<string, string> = {}
      for (const [target, filled] of Object.entries(updates)) {
        if (target === key) continue
        next[target] = filled == null ? '' : String(filled)
      }
      if (Object.keys(next).length) setEdits((prior) => ({ ...prior, ...next }))
      if (result?.protect && Object.keys(result.protect).length) {
        setRuleProtect((prior) => ({ ...prior, ...result.protect }))
      }
    }).catch((exc) => {
      // A rule that could not run is worth saying: the fields it would have
      // filled are still empty, and silence reads as "this field does nothing".
      setError(exc instanceof Error ? exc.message
        : 'The rule on this field could not run.')
    })
  }, [client, detail, edits])

  useEffect(() => { void load() }, [load])

  // Advisories belong to ONE save of ONE document. They are deliberately not
  // cleared in `load`, which runs immediately after a successful save and
  // would wipe the thing that save just raised.
  useEffect(() => { setSavedAdvisories([]) }, [documentId])

  const loadExtras = useCallback(async () => {
    // Best-effort and independent of each other: a repository without
    // signature or compound support must not fail to open a document.
    if (client.signatureStatus) {
      try {
        setSignatures(await client.signatureStatus(documentId))
      } catch { setSignatures(null) }
    }
    if (client.whereUsed) {
      try {
        setWhereUsed(await client.whereUsed(documentId))
      } catch { setWhereUsed(null) }
    }
    if (client.documentSource) {
      try {
        setSource(await client.documentSource(documentId))
      } catch { setSource(null) }
    } else {
      // A host that does not serve the lookup has settled the question: every
      // document here is edited in the repository's own writer.
      setSource(null)
    }
  }, [client, documentId])

  useEffect(() => { void loadExtras() }, [loadExtras])

  /*
   * What the panel opens on: the reader's own pick, else the release in force,
   * else the NEWEST version written.
   *
   * The last of those used to be `versions[0]`. The server returns the chain
   * ASCENDING, so that is the OLDEST — and `current_version_id` moves only on
   * promotion (see `dms/versioning.promote`), which means every document that
   * has not been approved yet has none. A draft SOP revised four times opened
   * on v1.0 and stayed there: the reader saw the first thing ever written,
   * with nothing on screen saying so, while v1.4 was the work. Newest-last is
   * also what the engine means by "the candidate" and what the approval
   * reviewer already falls back to.
   *
   * A document that HAS a release is unaffected — the release still wins over
   * later drafts, which is the point of a controlled repository.
   */
  const version = useMemo<DmsVersion | null>(() => {
    if (!detail) return null
    const wanted = selectedVersion || String(detail.document.current_version_id || '')
    return detail.versions.find((v) => String(v.id) === wanted)
      || detail.versions[detail.versions.length - 1] || null
  }, [detail, selectedVersion])

  // A transition that sends the document BACK collects its reason first.
  // `client.act` has always accepted a comment and the server has always
  // recorded it — nothing here asked for one, so every rejection reached its
  // owner saying only that it had been rejected.
  const [returning, setReturning] = useState<DmsAction | null>(null)
  const [reason, setReason] = useState('')
  // One queued re-index per visit is enough — the button re-arms on reopen.
  const [reindexQueued, setReindexQueued] = useState(false)
  // The directory, for USER-typed fields. Loaded once and shared by every
  // field, so a form with an owner and a reviewer costs one request.
  // The host usually has the directory already (the library loads it for its
  // own listing); fetch it only when opened standalone.
  const [ownUsers, setOwnUsers] = useState<DmsUser[] | undefined>(undefined)
  const users = givenUsers ?? ownUsers
  const nameOf = useCallback((key: string | null | undefined) => (
    personName(users, key)), [users])

  useEffect(() => {
    if (givenUsers || !client.listUsers) return
    let live = true
    void (async () => {
      try {
        const list = await client.listUsers!()
        if (live) setOwnUsers(list)
      } catch {
        // No directory is a degraded field, not a broken page.
      }
    })()
    return () => { live = false }
  }, [client, givenUsers])

  // ── the name, and changing it ───────────────────────────────────────────
  //
  // Filed documents inherit the FILE's name, which is a fact about somebody's
  // scanner rather than about the document — `Scan_20260812_0003.pdf` tells a
  // reader nothing and is what the whole library ends up looking like. The
  // title is the one name the product shows, so it is editable here, in place,
  // by anyone who may edit the document.
  const [renaming, setRenaming] = useState(false)
  const [draftTitle, setDraftTitle] = useState('')

  // ── approving without leaving ───────────────────────────────────────────
  //
  // An approver who opens the document to read it before deciding used to have
  // to remember where the queue was and find it again. If something is waiting
  // on this caller FOR THIS DOCUMENT, the decision belongs on the document.
  const [waiting, setWaiting] = useState<DmsPendingApproval | null>(null)
  const [waitingDetail, setWaitingDetail] = useState<DmsApprovalDetail | null>(null)

  /*
   * Re-runnable, and re-run after every lifecycle action.
   *
   * This used to be a bare `useEffect` keyed on the document — fetched once
   * when the panel opened and never again. But an `via: "approval"` action
   * (`approve` on an SOP) does not move `lifecycle_state`: it STAGES the
   * document and raises an approval instance, and the decision then lives in
   * the box this lookup draws. Fetching once meant the click produced a
   * reload that rendered the identical panel — the same state, the same
   * buttons, no box, no message — so the only readings available were "the
   * button is broken" or "click it again", and clicking again raised another
   * instance. The step that follows the click has to arrive with the click.
   */
  const loadWaiting = useCallback(async () => {
    if (!client.pendingApprovals) return
    try {
      const rows = await client.pendingApprovals()
      const mine = rows.find((row) => row.document_id === documentId) || null
      setWaiting(mine)
      if (!mine) { setWaitingDetail(null); return }
      if (!client.approvalDetail) return
      try {
        setWaitingDetail(await client.approvalDetail(mine.instance_id))
      } catch {
        // The box still offers the decision; only the diff is missing.
      }
    } catch {
      // Not an approver. The box simply never appears.
    }
  }, [client, documentId])

  useEffect(() => { void loadWaiting() }, [loadWaiting])

  // Something went RIGHT and is worth saying — a staged approval, mostly,
  // where nothing else on the panel visibly changes.
  const [notice, setNotice] = useState('')

  const run = useCallback(async (work: () => Promise<unknown>) => {
    setBusy(true)
    setError('')
    setNotice('')
    try {
      await work()
      await load()
      // The decision box belongs to the same click that raised it.
      await loadWaiting()
      onChanged?.()
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message
        : 'That could not be completed.')
    } finally {
      setBusy(false)
    }
  }, [load, loadWaiting, onChanged])

  if (error && !detail) {
    return <div style={{ padding: 24, color: T.error, fontSize: TYPE.body, lineHeight: 1.55 }}>{error}</div>
  }
  if (!detail) {
    return <div style={{ padding: 24, fontSize: TYPE.body, color: T.textSecondary }}>Loading…</div>
  }

  const doc = detail.document
  const state = String(doc.lifecycle_state || 'draft')
  const canRename = !!client.rename && !!detail.rights.edit
  const openInEditor = editorOpen(detail, selectedVersion)

  /*
   * History, newest first — and only the recent ones until asked.
   *
   * The server returns versions ASCENDING, which is right for the engine (the
   * newest is the last element, and "the candidate" means `versions[-1]`) and
   * wrong for a person: what somebody opens a version list to see is what
   * happened last, and on a document with thirty revisions that was thirty
   * rows down. Reversed for reading, capped so a long-lived SOP does not bury
   * every other section of this panel, and the button says how many are
   * hidden rather than being a bare "more".
   */
  const newestFirst = [...detail.versions].reverse()
  const shownVersions = allVersions
    ? newestFirst
    : newestFirst.slice(0, VERSIONS_SHOWN)
  const hiddenVersions = newestFirst.length - shownVersions.length
  const startRename = () => {
    if (!canRename) return
    setDraftTitle(String(doc.title || ''))
    setRenaming(true)
  }
  const revision = detail.revision
  const heldBy = String(doc.checked_out_by || '')
  // "Not the one in force" needs there to BE one. `current_version_id` moves
  // only on promotion, so a draft has none — and every version of it compared
  // unequal, which put "you are reading a version that is not in force, do not
  // act on it" over the only copy of a document nothing had superseded. A
  // document with nothing released says nothing here; its lifecycle chip
  // already says draft.
  const inForceId = String(doc.current_version_id || '')
  const isCurrent = !inForceId || String(version?.id || '') === inForceId
  // The two halves of a signed pair, whichever half is being read: the
  // executed copy made FROM this version, and the version this executed copy
  // was made from. Either may be absent — a campaign still running has no copy
  // yet, and a repository whose owner never granted the provenance column has
  // no back-link — and the notes below say only what they can see.
  const executedCopy = detail.versions.find(
    (v) => String(v.signed_from_version_id || '') === String(version?.id || '')
           && !!version?.id) || null
  const signedFrom = version?.source_kind === 'signed'
    ? detail.versions.find(
        (v) => String(v.id) === String(version?.signed_from_version_id || '')) || null
    : null
  const unconfirmed = detail.metadata.filter((m) => m.source === 'ai' && !m.confirmed_by)

  return (
    <div style={{ display: 'flex', height: '100%', minHeight: 0 }}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {!isCurrent && version ? (
          <div style={warnBarStyle}>
            You are reading version {version.version_label}, which is <strong>not</strong> the
            one in force. Do not act on it.
          </div>
        ) : null}
        {revision?.in_progress ? (
          // Said plainly, because the thing people get wrong about a
          // controlled document under revision is believing the draft is
          // already the rule. It is not: the released version stays in force
          // until the revision is approved, and that is what anyone
          // downloading this document gets.
          <div style={reviseBarStyle}>
            <strong>Revision in progress.</strong>{' '}
            Version {revision.candidate_label} has been written and is not yet
            approved. Version {revision.released_label || 'the released one'} stays
            in force until it is.
          </div>
        ) : null}
        {version?.source_kind === 'signed' ? (
          // The executed copy. Same content, same render payload, with the
          // collected signatures drawn into their lines — filed as a MINOR
          // version above the one that was signed, which stays in the
          // repository unchanged because it is the file the signature was
          // given against.
          <div style={noteBarStyle}>
            This is the <strong>executed copy</strong> — the same document with
            the signatures drawn into it.{' '}
            {signedFrom
              ? <>Version {signedFrom.version_label} above it is the file people
                  actually signed, kept unchanged.</>
              : <>The version it was made from is kept unchanged.</>}{' '}
            {signatures?.summary}
          </div>
        ) : signatures && signatures.signed > 0 ? (
          // The page and the panel disagreed, and both were telling the truth.
          //
          // A signature here is an audit-trail record (this repository's
          // provider applies no cryptography and rewrites no bytes), so the
          // filed PDF is exactly what was signed and its signature lines are
          // frozen at the moment of filing — which was before anybody signed.
          // The panel is live. Neither is wrong; nothing said they were
          // different things, so "signed" and "Not yet signed" sat side by side
          // on one screen.
          //
          // Re-rendering THIS copy to draw the signatures onto it would still
          // be the wrong fix — it would change the bytes the signature was
          // given against, and the manifest binds the signature to this file's
          // hash. The executed copy is a new version beside it, and the line
          // below is how a reader finds it.
          <div style={noteBarStyle}>
            The signature lines printed on this copy were fixed when it was
            filed, before it was signed — this is the file people signed, so it
            is not rewritten. <strong>{signatures.summary}</strong>{' '}
            {executedCopy
              ? <>Version {executedCopy.version_label} is the executed copy, with
                  the signatures drawn in.</>
              : <>The signatures themselves are listed on the right, and in the
                  signature manifest.</>}
          </div>
        ) : null}
        <SecureDocViewer
          client={client}
          documentId={documentId}
          version={version}
          rights={detail.rights}
          title={String(doc.title || '')}
          focus={focus}
        />
      </div>

      <aside style={sideStyle}>
        {/*
          * Unsaved metadata, said where it cannot be missed.
          *
          * The save used to sit at the BOTTOM of the Metadata section, which on
          * a type with seventeen fields is below the fold of a 340px sidebar —
          * so a person filled the form and had no visible way to keep it. It is
          * now a sticky bar at the top of the panel, it counts what is pending,
          * and it offers to discard rather than leaving that as something you
          * do by navigating away and hoping.
          */}
        {detail.rights.edit && Object.keys(edits).length > 0 ? (
          <div style={unsavedBarStyle}>
            <span>
              {Object.keys(edits).length} unsaved
              {' '}change{Object.keys(edits).length === 1 ? '' : 's'}
            </span>
            <span style={{ flex: 1 }} />
            <button type="button" disabled={busy} style={btnStyle}
              onClick={() => setEdits({})}>
              Discard
            </button>
            <button
              type="button"
              disabled={busy}
              style={btnPrimaryStyle}
              onClick={() => run(async () => {
                let advisories: DmsValidationFailure[] = []
                setSavedAdvisories([])
                try {
                  advisories = await client.saveMetadata(documentId, edits) || []
                } catch (saveError) {
                  // A refused rule names its field. Hold onto that before
                  // rethrowing, so the strip above says what is wrong AND the
                  // box that is wrong says it too.
                  const failures = metadataFailures(saveError)
                  if (failures.length) {
                    const byField: Record<string, DmsValidationFailure[]> = {}
                    for (const failure of failures) {
                      const key = String(failure.field || '')
                      if (!key) continue
                      byField[key] = [...(byField[key] || []), failure]
                    }
                    setFieldErrors(byField)
                  }
                  throw saveError
                }
                setEdits({})
                // The locks were a consequence of unsaved values; once they are
                // filed the reloaded document decides afresh.
                setRuleProtect({})
                // A 'W' or 'P' rule does not block, so this is the ONLY moment
                // it can be read. `run` reloads after this and clears the map,
                // so they are handed to the notice strip instead of the boxes.
                if (advisories.length) {
                  setSavedAdvisories(advisories.map((a) =>
                    `${a.title ? `${a.title}: ` : ''}${a.message}`))
                }
              })}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        ) : null}
        <header style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* The KIND first, and as a chip rather than a footnote: it is the
                single most load-bearing fact about a controlled document, and
                it used to be grey 12px text after the status. */}
            {detail.doctype ? (
              <div style={{ marginBottom: 6 }}>
                <FactChip tone="accent">{detail.doctype.title}</FactChip>
              </div>
            ) : null}
            {renaming ? (
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                <input
                  autoFocus
                  value={draftTitle}
                  aria-label="Document name"
                  onChange={(event) => setDraftTitle(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setRenaming(false)
                    if (event.key === 'Enter' && draftTitle.trim()) {
                      void run(async () => {
                        await client.rename!(documentId, draftTitle.trim())
                        setRenaming(false)
                      })
                    }
                  }}
                  style={{ ...inputStyle, fontSize: TYPE.heading, fontWeight: 600 }}
                />
                <button type="button" disabled={busy || !draftTitle.trim()}
                        style={btnPrimaryStyle}
                        onClick={() => run(async () => {
                          await client.rename!(documentId, draftTitle.trim())
                          setRenaming(false)
                        })}>
                  Save
                </button>
                <button type="button" disabled={busy} style={btnStyle}
                        onClick={() => setRenaming(false)}>
                  Cancel
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                {/*
                  * The name is the control.
                  *
                  * A word-wide "Rename" button beside a document title is a
                  * lot of furniture for something done once, and it is not
                  * where anybody looks: the thing people click when they want
                  * to change a name is the name. So the heading itself starts
                  * the rename when it can be renamed, and the pencil beside it
                  * is the affordance that says so — small, because it is a
                  * hint, not a command competing with the title.
                  */}
                <h2
                  style={{
                    margin: 0, fontSize: TYPE.title, fontWeight: 600,
                    lineHeight: 1.25, letterSpacing: '-0.02em',
                    flex: 1, minWidth: 0,
                    cursor: canRename ? 'text' : 'default',
                    borderRadius: 6,
                  }}
                  {...(canRename ? {
                    role: 'button',
                    tabIndex: 0,
                    title: 'Click to rename',
                    onClick: startRename,
                    onKeyDown: (event: React.KeyboardEvent) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        startRename()
                      }
                    },
                  } : {})}
                >
                  {String(doc.title || 'Untitled')}
                </h2>
                {canRename ? (
                  <button type="button" style={iconBtnStyle}
                          aria-label="Rename this document"
                          title="Rename this document"
                          onClick={startRename}>
                    <Icon name="pencil" size={15} />
                  </button>
                ) : null}
              </div>
            )}
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <StatusChip tone={STATE_CHIP_TONE[state] || 'neutral'}>
                {humaniseState(state)}
              </StatusChip>
              {doc.owner_user ? (
                <span style={{ fontSize: TYPE.small, color: T.textFaint }}>
                  Looked after by {String(doc.owner_user) === userKey
                    ? 'you' : nameOf(String(doc.owner_user))}
                </span>
              ) : null}
            </div>
            {/* The whole point of the chip is answering "so what?" — say it. */}
            {STATE_MEANING[state] ? (
              <p style={{ ...explainStyle, marginTop: 6 }}>{STATE_MEANING[state]}</p>
            ) : null}
            {!detail.rights.download ? (
              <p style={{ ...explainStyle, marginTop: 6 }}>{READ_ONLY_EXPLANATION}</p>
            ) : null}
          </div>
          {onClose ? (
            <button type="button" style={btnStyle} onClick={() => {
              // The same courtesy the editor extends: metadata typed and not
              // saved is work, and navigating away is not consent to lose it.
              if (Object.keys(edits).length && typeof window !== 'undefined'
                  && !window.confirm(
                    'You have changed some details and not saved them. '
                    + 'Leave anyway and lose the changes?')) return
              onClose()
            }}>Close</button>
          ) : null}
        </header>

        {error ? <ActionError text={error} /> : null}
        {notice && !error ? <p style={noticeStyle}>{notice}</p> : null}
        {/* Rules that did not refuse the save but that somebody should read.
            They have nowhere else to appear: severity 'W' and 'P' do not
            block, so the save response is the only place they exist. */}
        {savedAdvisories.length ? (
          <p style={advisoryStyle}>
            {savedAdvisories.length === 1 ? savedAdvisories[0]
              : `Saved, with ${savedAdvisories.length} things worth reading: `
                + savedAdvisories.join('; ')}
          </p>
        ) : null}

        {heldBy ? (
          <p style={noteStyle}>
            {heldBy === userKey
              ? 'You are writing the next version. Add a new file when you are '
                + 'finished, or cancel below to let someone else take it on.'
              : `${nameOf(heldBy)} is writing the next version. Nobody else can `
                + 'change it until they are finished — what you see here still applies.'}
          </p>
        ) : null}

        {/* Waiting on THIS caller, for THIS document. An approver who opened
            the document to read it before deciding should not have to go and
            find the queue again. */}
        {waiting && client.decideApproval ? (
          <div style={approvalBoxStyle}>
            <strong style={{ display: 'block', fontSize: TYPE.meta, marginBottom: 4 }}>
              Your approval is needed
            </strong>
            <span style={{ fontSize: TYPE.small, lineHeight: 1.55 }}>
              {waiting.submitted_by
                ? `${nameOf(waiting.submitted_by)} sent this for approval.` : 'This was sent for approval.'}
              {waiting.pending_roles ? ` It is with ${waiting.pending_roles}.` : ''}
              {' '}Nothing changes for readers until every approver has agreed.
            </span>
            {waitingDetail ? (
              <div style={{ marginTop: 12 }}>
                <DmsApprovalReview
                  client={client}
                  instanceId={waiting.instance_id}
                  detail={waitingDetail}
                  onError={setError}
                />
              </div>
            ) : null}
            <div style={{ marginTop: 12 }}>
              <ApprovalDecision
                client={client}
                instanceId={waiting.instance_id}
                title={String(doc.title || 'this document')}
                ownerName={waiting.submitted_by ? nameOf(waiting.submitted_by) : ''}
                onDone={() => {
                  setWaiting(null)
                  setWaitingDetail(null)
                  void load()
                  onChanged?.()
                  onDecided?.()
                }}
              />
            </div>
          </div>
        ) : null}

        <Section title="What you can do">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {/*
              * FIRST, and primary.
              *
              * Carrying on writing a draft is the single most common thing
              * anybody does to a document in this repository, and it used to
              * be a plain button at the bottom of the version list four
              * sections down the panel — findable only by somebody who
              * already knew it was there. It belongs with the other things
              * you can do to the document, at the top, and it belongs looking
              * like the one you probably came here for.
              */}
            {/*
              * A document a FORM filed is edited in that form, never here.
              *
              * The repository's writer edits wording; the form edits the
              * record the wording is drawn from. Opening the writer on an
              * agreement filed from a transaction forks the two apart — the
              * document would say one thing and the record another, with the
              * signatures still attached to the document. So where a source
              * exists the primary action leads to it, and the ordinary editor
              * is not offered at all.
              *
              * Nothing is drawn until the lookup has answered (`source` is
              * undefined), or the button would change under the reader's
              * cursor a moment after the panel opens.
              */}
            {source && onOpenSource && detail.rights.edit ? (
              <button
                type="button"
                disabled={busy}
                style={btnPrimaryStyle}
                title={`This document is filled in on its record in `
                  + `${source.obj_name}. Editing it there keeps the document `
                  + `and the record saying the same thing.`}
                onClick={() => onOpenSource(source)}
              >
                Edit on its record
              </button>
            ) : source === null && onAuthor && openInEditor ? (
              <button
                type="button"
                disabled={busy}
                style={btnPrimaryStyle}
                title={openInEditor.hint}
                onClick={() => onAuthor({
                  kind: 'open',
                  documentId,
                  versionId: openInEditor.versionId,
                })}
              >
                {openInEditor.label}
              </button>
            ) : null}
            {/* A surface that cannot navigate (the designer's preview) still
                owes the reader the reason there is no Edit here — silence
                reads as a missing button. */}
            {source && !onOpenSource ? (
              <span style={mutedStyle}>
                This document is filled in on its record in {source.obj_name},
                and is edited there.
              </span>
            ) : null}
            {detail.actions.length === 0 && !openInEditor && !source ? (
              <span style={mutedStyle}>There is nothing for you to do here.</span>
            ) : detail.actions.map((action) => (
              // "Send the revision for approval" is only a thing to do once
              // there IS a revision. The server refuses it either way; a
              // button that exists to produce an error is a button that
              // should not have been drawn.
              action.action === 'revise' && !revision?.in_progress ? null : (
              <button
                key={action.action}
                type="button"
                disabled={busy}
                style={btnStyle}
                title={action.via === 'approval'
                  ? 'This sends it for approval. Nothing changes for readers '
                    + 'until every approver has agreed.'
                  : isReturningAction(action)
                    ? 'This sends it back. You will be asked why.'
                    : `This moves it to: ${humaniseState(action.to).toLowerCase()}`}
                onClick={() => {
                  if (isReturningAction(action)) {
                    setReturning(action)
                    setReason('')
                    return
                  }
                  void run(async () => {
                    const result = await client.act(documentId, action.action)
                    // `staged` means the document did NOT move: it is with its
                    // approvers now. Say so, because the panel alone cannot —
                    // the state chip and the buttons are exactly as they were.
                    if (result?.staged) {
                      setNotice('Sent for approval. It stays where it is, and '
                        + 'nothing changes for readers, until every approver '
                        + 'has agreed.')
                    }
                    return result
                  })
                }}
              >
                {actionLabel(action)}
                {action.via === 'approval' ? ' →' : ''}
                {isReturningAction(action) ? '…' : ''}
              </button>
            )))}
            {detail.rights.edit && !heldBy ? (
              <button type="button" disabled={busy} style={btnStyle}
                      onClick={() => run(() => client.checkOut(documentId))}>
                Check out
              </button>
            ) : null}
            {heldBy && (heldBy === userKey || detail.rights.edit) ? (
              <button type="button" disabled={busy} style={btnStyle}
                      onClick={() => run(() => client.cancelCheckOut(documentId))}>
                Cancel check-out
              </button>
            ) : null}
            {canReindex && client.reindex ? (
              <button type="button" disabled={busy || reindexQueued} style={btnStyle}
                      title="Read this document again so searching and questions pick up its latest content"
                      onClick={() => {
                        void (async () => {
                          setBusy(true)
                          try {
                            await client.reindex!(documentId)
                            setReindexQueued(true)
                          } catch (reindexError) {
                            setError(reindexError instanceof Error
                              ? reindexError.message
                              : 'The re-index could not be started.')
                          } finally {
                            setBusy(false)
                          }
                        })()
                      }}>
                {reindexQueued ? 'Queued' : 'Rebuild this document\u2019s search'}
              </button>
            ) : null}
          </div>
          {reindexQueued ? (
            <p style={noteStyle}>
              Being read again in the background. Its content will be findable
              in a moment.
            </p>
          ) : null}
          {returning ? (
            <div style={reasonBoxStyle}>
              <label style={labelStyle}>
                Why are you sending it back?
                <textarea
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  rows={3}
                  placeholder="The owner sees this. Say what has to change."
                  style={reasonInputStyle}
                />
              </label>
              <div style={{ ...mutedStyle, marginTop: 6 }}>
                Required. It is kept with the document as part of its record.
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <button
                  type="button"
                  disabled={busy || !reason.trim()}
                  style={btnStyle}
                  title={reason.trim() ? undefined
                    : 'Without a reason there is nothing for the owner to act on.'}
                  onClick={() => {
                    const chosen = returning
                    void run(async () => {
                      await client.act(documentId, chosen.action, reason.trim())
                      setReturning(null)
                      setReason('')
                    })
                  }}
                >
                  {busy ? 'Recording…' : 'Send it back'}
                </button>
                <button type="button" disabled={busy} style={btnStyle}
                        onClick={() => { setReturning(null); setReason('') }}>
                  Cancel
                </button>
              </div>
            </div>
          ) : null}
        </Section>

        {unconfirmed.length > 0 ? (
          <div style={aiBannerStyle}>
            <strong style={{ display: 'block', marginBottom: 4, fontSize: TYPE.meta }}>
              {unconfirmed.length} suggested detail{unconfirmed.length === 1 ? '' : 's'} — nobody has checked {unconfirmed.length === 1 ? 'it' : 'them'} yet
            </strong>
            <span style={{ fontSize: TYPE.small, lineHeight: 1.55 }}>
              These were read out of the document automatically. They will not
              be treated as fact until somebody confirms them.
            </span>
            {detail.rights.edit ? (
              <div style={{ marginTop: 6 }}>
                <button
                  type="button"
                  disabled={busy}
                  style={btnStyle}
                  onClick={() => run(() => client.confirmMetadata(
                    documentId, unconfirmed.map((m) => m.field_key)))}
                >
                  {unconfirmed.length === 1 ? 'Looks right' : 'They all look right'}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        <Section title="Where it is filed">
          <FiledIn
            client={client}
            documentId={documentId}
            folders={folders || []}
            folderId={String(doc.folder_id || '')}
            canEdit={Boolean(detail.rights.edit)}
            onMoved={() => { void load(); onChanged?.() }}
          />
        </Section>

        <Section title="The file">
          <FileFacts detail={detail} />
        </Section>

        <Section title="Details">
          {detail.metadata.length === 0 && !(detail.doctype?.fields || []).length ? (
            <span style={mutedStyle}>This kind of document asks for no extra details.</span>
          ) : (
            <MetadataList
              detail={detail}
              client={client}
              users={users}
              documentId={documentId}
              edits={edits}
              ruleProtect={ruleProtect}
              fieldErrors={fieldErrors}
              onEdit={editMetadata}
            />
          )}
        </Section>

        <Section title={`History (${detail.versions.length})`}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {shownVersions.map((v) => {
              const current = String(v.id) === String(doc.current_version_id || '')
              return (
                <li key={v.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedVersion(String(v.id))}
                    style={{
                      ...versionRowStyle,
                      background: String(v.id) === String(version?.id || '')
                        ? T.surface2 : 'transparent',
                    }}
                  >
                    <span style={{ fontWeight: current ? 700 : 500 }}>
                      v{v.version_label}
                      {current ? ' · in force now' : ''}
                      {/* Before the label existed, the only difference between
                          the copy people signed and the copy carrying their
                          signatures was a decimal point. */}
                      {v.source_kind === 'signed' ? ' · executed copy' : ''}
                      {v.is_editable && v.source_kind !== 'signed'
                        ? ' · written here' : ''}
                    </span>
                    <span style={mutedStyle}>
                      {formatWhen(v.created_at)}
                      {v.created_by ? ` · ${String(v.created_by) === userKey
                        ? 'you' : nameOf(String(v.created_by))}` : ''}
                      {' · '}{formatBytes(v.size_bytes)}
                      {v.scan_status && v.scan_status !== 'clean'
                        ? ` · not yet checked for viruses` : ''}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
          {hiddenVersions > 0 ? (
            <button type="button" style={{ ...btnStyle, width: '100%', marginTop: 6 }}
                    onClick={() => setAllVersions(true)}>
              Show {hiddenVersions} older version{hiddenVersions === 1 ? '' : 's'}
            </button>
          ) : null}
          {onAuthor ? (
            <AuthoringActions
              documentId={documentId}
              detail={detail}
              onAuthor={onAuthor}
            />
          ) : null}
        </Section>

        <DocumentReview
          client={client}
          documentId={documentId}
          detail={detail}
          versionId={String(version?.id || '')}
          busy={busy}
          onChanged={() => { void load(); onChanged?.() }}
        />

        <Section title="Where this can be found">
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13 }}>
            {Object.entries(detail.search_flags).map(([flag, on]) => (
              <li key={flag} style={{ color: on ? 'var(--color-text)' : 'var(--color-text-secondary)' }}>
                {FLAG_LABELS[flag] || flag}: {on ? 'yes' : 'no'}
              </li>
            ))}
            <li style={{ color: 'var(--color-text-secondary)' }}>
              AI handling: {AI_LABELS[detail.ai_mode] || detail.ai_mode}
            </li>
          </ul>
        </Section>

        {detail.compound && (
          <CompoundPanel
            compound={detail.compound}
            client={client}
            documentId={documentId}
            canEdit={detail.rights.edit}
            onChanged={() => { void load(); onChanged?.() }}
          />
        )}

        {whereUsed && (
          <WhereUsedPanel rows={whereUsed.parents}
                          hidden={whereUsed.hidden_parents} />
        )}

        {signatures && (
          <SignaturePanel
            status={signatures}
            client={client}
            documentId={documentId}
            canSign={detail.rights.sign}
            // `load` as well as `loadExtras`: the signature that COMPLETES a
            // campaign files one more version — the executed copy, the same
            // document with the signatures drawn into their lines — and a
            // panel that refreshed only the signature list would go on showing
            // the unsigned rendition of the version just signed.
            onChanged={() => { void load(); void loadExtras(); onChanged?.() }}
          />
        )}
      </aside>
    </div>
  )
}

/**
 * What the file IS — type, name, size, pages, digest.
 *
 * Deliberately NOT a metadata field. A field a user can type into drifts from
 * the bytes on disk, and the one thing a reader of a controlled document must
 * be able to trust is what they are actually looking at. These come from the
 * version row the server built when it sniffed the upload.
 */
function FileFacts({ detail }: { detail: DmsDocumentDetail }) {
  const info = detail.file_info || {}
  const rules = detail.file_rules || {}
  if (!info.content_type && !info.filename && !info.size_bytes) {
    return <span style={mutedStyle}>This document has no file yet.</span>
  }
  const rows: Array<[string, string]> = [
    ['Type', info.type_label || info.content_type || '—'],
    ['File name', info.filename || '—'],
    ['Size', info.size_label || formatBytes(Number(info.size_bytes || 0))],
  ]
  if (info.page_count) rows.push(['Pages', String(info.page_count)])
  const accepted = [
    (rules.extensions || []).length
      ? (rules.extensions || []).map((e) => '.' + e).join(', ') : '',
    rules.max_size_mb ? `up to ${rules.max_size_mb} MB` : '',
    rules.min_size_kb ? `at least ${rules.min_size_kb} KB` : '',
  ].filter(Boolean).join(' · ')
  return (
    <>
      <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr',
                   gap: '4px 12px', fontSize: 13 }}>
        {rows.map(([label, value]) => (
          <div key={label} style={{ display: 'contents' }}>
            <dt style={{ ...mutedStyle, whiteSpace: 'nowrap' }}>{label}</dt>
            <dd style={{ margin: 0, wordBreak: 'break-word' }}>{value}</dd>
          </div>
        ))}
      </dl>
      {accepted ? (
        <p style={{ ...mutedStyle, marginTop: 8, marginBottom: 0 }}>
          This type accepts {accepted}.
        </p>
      ) : null}
    </>
  )
}

const FLAG_LABELS: Record<string, string> = {
  metadata_search: 'Appears in search',
  full_text_search: 'Text is indexed',
  ai_qa: 'Answerable by the assistant',
}

const AI_LABELS: Record<string, string> = {
  full: 'full text is used',
  redacted: 'identifiers removed first',
  summary_only: 'only a summary is used',
  none: 'never sent to a model',
}

/**
 * A refusal, rendered so it can be acted on.
 *
 * The validate gate returns EVERY problem at once — a kind of document with nine
 * mandatory fields refuses with nine of them joined by semicolons — because
 * fixing them one round-trip at a time is worse. But a paragraph of nine
 * clauses in small red text is a wall, so the list is rendered as a list, with
 * the lead-in kept as the sentence it is.
 */
function ActionError({ text }: { text: string }) {
  const [lead, rest] = splitRefusal(text)
  const items = rest ? rest.split(';').map((s) => s.trim()).filter(Boolean) : []
  if (items.length < 2) return <p style={errStyle}>{text}</p>
  return (
    <div style={errStyle}>
      <strong style={{ display: 'block', marginBottom: 4 }}>{lead}</strong>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {items.map((item) => <li key={item}>{item}</li>)}
      </ul>
    </div>
  )
}

/** Split "not ready for review: a; b; c" into its lead-in and its list. Any
 *  other message has no colon-then-list shape and is left whole. */
function splitRefusal(text: string): [string, string] {
  const at = text.indexOf(': ')
  if (at < 0) return [text, '']
  return [`${text.slice(0, at)}:`, text.slice(at + 2)]
}

/** What a system-filled field that is still empty is waiting for. A bare dash
 *  would read as "nobody bothered", when in fact nobody was ever asked. */
function managedPlaceholder(field?: DmsField): string {
  if (!field) return '—'
  if (generatedOnFiling(field)) return 'Numbered when the document is filed'
  return String(field.default_preview || '') || 'Filled in automatically'
}

/** `mandatory` crosses the wire as whatever the column held — 1, '1', true. */
function truthyFlag(value: unknown): boolean {
  return value === true || value === 1 || value === '1'
    || String(value).toLowerCase() === 'true'
}

function MetadataList({ detail, edits, onEdit, client, documentId, users,
                       ruleProtect, fieldErrors }: {
  detail: DmsDocumentDetail
  edits: Record<string, string>
  onEdit: (key: string, value: string) => void
  client: DmsClient
  documentId: string
  users?: DmsUser[]
  /** Fields an on-change rule locked (1) or unlocked (0) for this session. */
  ruleProtect?: Record<string, 0 | 1>
  /** What the last refused save said about each field. */
  fieldErrors?: Record<string, DmsValidationFailure[]>
}) {
  const byKey = new Map(detail.metadata.map((m) => [m.field_key, m]))
  const fields = detail.doctype?.fields || []
  const fieldByKey = new Map(fields.map((f) => [f.field_key, f]))
  const mandatory = new Set(
    fields.filter((f) => truthyFlag(f.mandatory)).map((f) => f.field_key))
  // Every current value in one bag: what a lookup filter narrows by
  // ({form.OTHER_FIELD}) and what a composite display format reads its
  // companion column out of. Unsaved edits win — they are what the reader is
  // looking at.
  const formValues: Record<string, unknown> = {}
  for (const row of detail.metadata) {
    if (row.value != null) formValues[String(row.field_key)] = row.value
  }
  Object.assign(formValues, edits)

  const rows = fields.length > 0
    ? fields.map((f) => byKey.get(f.field_key) || {
        field_key: f.field_key, title: f.title, data_type: f.data_type,
        value: '', secure: Boolean(f.secure_json),
      })
    : detail.metadata

  return (
    <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
      {rows.map((row) => {
        const field = fieldByKey.get(row.field_key)
        // System-filled by the document TYPE. Never editable, whatever rights
        // the reader holds — the value is the repository's statement, not
        // theirs, and the server discards anything posted for it anyway.
        const managed = Boolean(field && isProtectedField(field))
        // A rule's lock wins over the static setting in BOTH directions: 1
        // locks a field the type left open, 0 opens one the rule had locked
        // earlier in the same session.
        const locked = ruleProtect?.[row.field_key]
        const editable = detail.rights.edit && !row.masked
          && (locked === 0 || (!managed && locked !== 1))
        const shown = edits[row.field_key] ?? (row.value == null ? '' : String(row.value))
        return (
          <div key={row.field_key}>
            <dt style={{ fontSize: TYPE.small, color: T.textFaint, marginBottom: 3 }}>
              {row.title}
              {/* A mandatory field that is empty is why a submit will be
                  refused, so it says so HERE rather than only in the error
                  the review gate returns nine fields later. */}
              {mandatory.has(row.field_key) ? (
                <b style={{ color: T.error }} title="This has to be filled in"> *</b>
              ) : null}
              {row.secure ? ' 🔒' : ''}
              {managed ? (
                <span style={{ color: T.textFaint }}
                      title="This kind of document fills this in — it is not typed here.">
                  {' '}· set automatically
                </span>
              ) : null}
              {row.source === 'ai' && !row.confirmed_by ? (
                <span style={{ color: T.accent, fontWeight: 600 }}
                      title="Read out of the document automatically. Nobody has checked it yet.">
                  {' '}· suggested{row.confidence != null
                    ? ` (${Math.round(Number(row.confidence) * 100)}% sure)` : ''}
                </span>
              ) : null}
            </dt>
            <dd style={{ margin: 0 }}>
              {row.is_image ? (
                <MetadataImage row={row} client={client} documentId={documentId}
                               editable={Boolean(detail.rights.edit) && !row.masked}
                               pending={edits[row.field_key]}
                               onPick={(dataUrl) => onEdit(row.field_key, dataUrl)} />
              ) : row.masked ? (
                <span style={{ fontSize: TYPE.meta, letterSpacing: 1, color: T.textSecondary }}
                      title="Hidden from your role">
                  {String(row.value || '••••')}
                </span>
              ) : editable ? (
                <MetadataFieldInput
                  field={field}
                  dataType={row.data_type}
                  value={shown}
                  users={users}
                  client={client}
                  doctypeId={String(detail.doctype?.id || '')}
                  formValues={formValues}
                  onChange={(next) => onEdit(row.field_key, next)}
                  style={inputStyle}
                />
              ) : (
                // Read-only, and for a USER field that means the NAME. Showing
                // the stored key would be showing our bookkeeping to somebody
                // who asked who owns the document.
                <span style={{ fontSize: TYPE.meta }}>
                  {shown ? (
                    // Through the shared <DisplayCell> when the field declares
                    // a format, so a status reads as a coloured tag here
                    // exactly as it does in the library list.
                    <MetadataValue field={field} value={shown}
                                   dataType={row.data_type} users={users}
                                   row={formValues} />
                  ) : (managed ? managedPlaceholder(field) : '—')}
                </span>
              )}
              {row.error ? (
                <div style={{ fontSize: TYPE.small, color: T.error, marginTop: 3 }}>{row.error}</div>
              ) : null}
              {(fieldErrors?.[row.field_key] || []).map((failure, i) => (
                <div key={i}
                     style={{ fontSize: TYPE.small, marginTop: 3,
                              color: (failure.severity || 'E') === 'E' ? T.error
                                : failure.severity === 'W' ? T.warning
                                  : T.textSecondary }}>
                  {failure.message}
                </div>
              ))}
            </dd>
          </div>
        )
      })}
    </dl>
  )
}

/**
 * An image-valued metadata field — a scanned identity document, a signature, a
 * site photograph.
 *
 * The bytes never arrive with the metadata list; they come from their own
 * endpoint, which re-checks the field's reveal roles on every fetch. So a
 * reader outside those roles sees that a picture EXISTS and nothing else —
 * which is the honest answer, because pretending the field is empty would make
 * a missing scan and a withheld one look identical to the person chasing it.
 */
function MetadataImage({ row, client, documentId, editable, pending, onPick }: {
  row: DmsMetadataValue
  client: DmsClient
  documentId: string
  editable?: boolean
  pending?: string
  onPick?: (dataUrl: string) => void
}) {
  const [open, setOpen] = useState(false)
  const picker = editable && onPick ? (
    <ImagePicker onPick={onPick} replacing={Boolean(row.has_image)} />
  ) : null
  if (pending) {
    return (
      <div style={{ display: 'grid', gap: 4 }}>
        <img src={pending} alt="" style={{ maxWidth: '100%', maxHeight: 200,
                                           borderRadius: 4 }} />
        <span style={mutedStyle}>Not saved yet — use Save metadata.</span>
        {picker}
      </div>
    )
  }
  if (!row.has_image) {
    return (
      <div style={{ display: 'grid', gap: 4 }}>
        <span style={{ ...mutedStyle, fontSize: 14 }}>No image</span>
        {picker}
      </div>
    )
  }
  if (row.masked || !row.image || !client.metadataImageUrl) {
    return (
      <span style={{ fontSize: 14 }}
            title="An image is stored here, but you are not permitted to see it">
        🔒 Image withheld
      </span>
    )
  }
  const src = client.metadataImageUrl(documentId, row.image.field_key)
  const isPicture = String(row.image.content_type || '').startsWith('image/')
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      {isPicture && open ? (
        <img
          src={src}
          alt={row.title}
          style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 4,
                   border: '1px solid var(--color-border)' }}
        />
      ) : null}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {isPicture ? (
          <button type="button" style={inputStyle}
                  onClick={() => setOpen((v) => !v)}>
            {open ? 'Hide' : 'Show image'}
          </button>
        ) : null}
        <a href={src} target="_blank" rel="noreferrer"
           style={{ fontSize: 13 }}>
          {row.image.filename || 'Open'}
        </a>
        <span style={mutedStyle}>{formatBytes(row.image.size_bytes || 0)}</span>
      </div>
      {picker}
    </div>
  )
}

/**
 * Pick a replacement image. Read into a data URL client-side rather than posted
 * as multipart, so an image value saves through the SAME metadata endpoint as
 * every other field — one save, one set of validations, one audit line, and no
 * half-saved record where the picture landed and the rest did not.
 */
function ImagePicker({ onPick, replacing }: {
  onPick: (dataUrl: string) => void
  replacing: boolean
}) {
  const [error, setError] = useState('')
  return (
    <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center',
                    fontSize: 13, cursor: 'pointer' }}>
      <span style={{ ...inputStyle, cursor: 'pointer' }}>
        {replacing ? 'Replace image' : 'Choose image'}
      </span>
      <input
        type="file"
        accept="image/*,application/pdf"
        style={{ display: 'none' }}
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ''
          if (!file) return
          setError('')
          const reader = new FileReader()
          reader.onerror = () => setError('That file could not be read.')
          reader.onload = () => {
            const result = String(reader.result || '')
            if (result.startsWith('data:')) onPick(result)
            else setError('That file could not be read.')
          }
          reader.readAsDataURL(file)
        }}
      />
      {error ? <span style={{ color: T.error }}>{error}</span> : null}
    </label>
  )
}

/**
 * Approve, or send it back, from the document itself.
 *
 * The same two asymmetric decisions the queue offers, and the same rule about
 * the reason: the engine accepts an empty comment, this does not, because a
 * document returned with nothing to act on is a wasted cycle.
 */
function ApprovalDecision({ client, instanceId, title, ownerName, onDone }: {
  client: DmsClient
  instanceId: string
  title: string
  ownerName: string
  onDone: () => void
}) {
  const [sendingBack, setSendingBack] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const decide = async (decision: 'approve' | 'reject') => {
    if (decision === 'reject' && !reason.trim()) {
      setError(`Say why you are sending it back. It goes to ${ownerName || 'its owner'}, `
               + 'and there is nothing else for them to work from.')
      return
    }
    setBusy(true)
    setError('')
    try {
      const result = await client.decideApproval!({
        instanceId, decision, comment: reason.trim(),
      })
      const status = String(result.status || '')
      setNotice(
        status === 'approved' ? `Approved. "${title}" is now the version in force.`
          : status === 'rejected'
            ? `Sent back to ${ownerName || 'its owner'} with your reason.`
            : 'Your approval is recorded. It is now with the next approver, '
              + 'so nothing has changed for readers yet.')
      onDone()
    } catch (decideError) {
      setError(decideError instanceof Error ? decideError.message
        : 'The decision could not be recorded.')
    } finally {
      setBusy(false)
    }
  }

  if (notice) return <p style={{ ...noteStyle, color: T.success }}>{notice}</p>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {sendingBack ? (
        <div>
          <label style={labelStyle}>Why are you sending it back?</label>
          <textarea
            autoFocus
            value={reason}
            rows={3}
            onChange={(event) => setReason(event.target.value)}
            placeholder={ownerName ? `${ownerName} sees this. Say what has to change.`
              : 'The owner sees this. Say what has to change.'}
            style={reasonInputStyle}
          />
          <div style={{ ...mutedStyle, marginTop: 6 }}>
            Required. It is kept with the document as part of its record.
          </div>
        </div>
      ) : null}
      {error ? <div style={errStyle}>{error}</div> : null}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {sendingBack ? (
          <>
            <button type="button" disabled={busy} style={btnDangerStyle}
                    onClick={() => void decide('reject')}>
              {busy ? 'Recording…' : `Send back${ownerName ? ` to ${ownerName}` : ''}`}
            </button>
            <button type="button" disabled={busy} style={btnStyle}
                    onClick={() => { setSendingBack(false); setReason(''); setError('') }}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <button type="button" disabled={busy} style={btnPrimaryStyle}
                    onClick={() => void decide('approve')}>
              {busy ? 'Recording…' : 'Approve'}
            </button>
            <button type="button" disabled={busy} style={btnStyle}
                    onClick={() => { setSendingBack(true); setError('') }}>
              Send back{ownerName ? ` to ${ownerName}` : ''}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

/** Where the document is filed, and moving it without going back to the list. */
function FiledIn({ client, documentId, folders, folderId, canEdit, onMoved }: {
  client: DmsClient
  documentId: string
  folders: DmsFolder[]
  folderId: string
  canEdit: boolean
  onMoved: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  // The SAME order as the rail — the shared helper, not a second sort. A
  // folder third in the sidebar and first in this menu is two libraries.
  const ordered = useMemo(() => orderFolders(folders), [folders])

  if (!folders.length) return null
  const current = folders.find((f) => String(f.id) === folderId)

  if (!canEdit || !client.move) {
    return (
      <div>
        <div style={{ fontSize: TYPE.small, color: T.textFaint }}>Filed in</div>
        <div style={{ fontSize: TYPE.meta, marginTop: 2 }}>
          {current?.name || 'Not filed in a folder'}
        </div>
      </div>
    )
  }

  return (
    <div>
      <label style={labelStyle} htmlFor="dms-filed-in">Filed in</label>
      <select
        id="dms-filed-in"
        value={folderId}
        disabled={busy}
        style={{ ...inputStyle, width: '100%' }}
        onChange={async (event) => {
          const target = event.target.value
          setBusy(true)
          setError('')
          try {
            await client.move!(documentId, target)
            onMoved()
          } catch (moveError) {
            // A folder that refuses this kind says so in a sentence; it must
            // reach the person who tried to file it there.
            setError(moveError instanceof Error ? moveError.message
              : 'It could not be filed there.')
          } finally {
            setBusy(false)
          }
        }}
      >
        <option value="">Not in a folder</option>
        {ordered.map(({ folder, depth }) => (
          <option key={folder.id} value={String(folder.id)}>
            {'\u00a0\u00a0'.repeat(depth) + (depth ? '└ ' : '') + folder.name}
          </option>
        ))}
      </select>
      {error ? <div style={errStyle}>{error}</div> : null}
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginTop: 16 }}>
      <h3 style={{ margin: '0 0 8px', fontSize: TYPE.meta, fontWeight: 600,
                   color: T.text }}>
        {title}
      </h3>
      {children}
    </section>
  )
}

const sideStyle: React.CSSProperties = {
  /* Widened with the type scale: 340px was set for 11-12px text and clipped
     the moment anything read at 15px. */
  width: 380,
  flexShrink: 0,
  borderLeft: `1px solid ${T.border}`,
  padding: 16,
  overflow: 'auto',
  background: T.surface,
}

const unsavedBarStyle: React.CSSProperties = {
  position: 'sticky',
  top: -16,
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  margin: '-16px -16px 12px',
  padding: '10px 16px',
  fontSize: TYPE.small,
  background: T.warningWeak,
  borderBottom: `1px solid ${T.warning}`,
  color: T.warning,
}

/**
 * How many versions the history shows before it asks.
 *
 * Ten is about a year of a document that is revised monthly — far enough back
 * to answer "what changed recently" without a controlled SOP with a decade of
 * history pushing everything below it off the panel.
 */
const VERSIONS_SHOWN = 10

const btnStyle: React.CSSProperties = sharedBtnStyle

/*
 * A square button carrying one glyph.
 *
 * For an action that is obvious from what it sits beside — the pencil next to
 * a document's name — where a word would take more room than the thing it
 * describes and would compete with a title it is meant to serve.
 */
const iconBtnStyle: React.CSSProperties = {
  ...sharedBtnStyle,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 'none',
  width: 30,
  height: 30,
  padding: 0,
}

const inputStyle: React.CSSProperties = { ...sharedInputStyle, width: '100%' }

const versionRowStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 2,
  width: '100%',
  padding: '8px 10px',
  border: 'none',
  borderRadius: RADIUS.md,
  textAlign: 'left',
  cursor: 'pointer',
  fontSize: TYPE.meta,
  fontFamily: 'inherit',
  color: T.text,
}

const mutedStyle: React.CSSProperties = {
  fontSize: TYPE.small,
  lineHeight: 1.5,
  color: T.textSecondary,
}

const reasonBoxStyle: React.CSSProperties = {
  marginTop: 10,
  padding: 12,
  border: `1px solid ${T.border}`,
  borderRadius: RADIUS.md,
}

const reasonInputStyle: React.CSSProperties = {
  ...sharedInputStyle,
  display: 'block',
  width: '100%',
  marginTop: 6,
  fontSize: TYPE.meta,
  lineHeight: 1.5,
  boxSizing: 'border-box',
}

const noteStyle: React.CSSProperties = {
  margin: '12px 0 0',
  padding: '10px 12px',
  borderRadius: RADIUS.md,
  fontSize: TYPE.small,
  lineHeight: 1.55,
  color: T.textSecondary,
  background: T.surface2,
}

const warnBarStyle: React.CSSProperties = {
  padding: '10px 14px',
  fontSize: TYPE.meta,
  lineHeight: 1.5,
  background: T.warningWeak,
  borderBottom: `1px solid ${T.warning}`,
  color: T.warning,
}

/*
 * A revision under way. Informational, NOT a warning: nothing is wrong and
 * nothing is required of the reader — the document in force is still the
 * document in force. Amber here would say "be careful what you are reading",
 * which is the message the bar above it exists to give and is not this one.
 */
const reviseBarStyle: React.CSSProperties = {
  padding: '10px 14px',
  fontSize: TYPE.meta,
  lineHeight: 1.5,
  background: T.infoWeak,
  borderBottom: `1px solid ${T.info}`,
  color: T.text,
}

/*
 * The filed copy predates its signatures. Informational, like the revision bar
 * and for the same reason: nothing is wrong and nothing is required of the
 * reader. Amber would say "be careful what you are reading" about a file that
 * is precisely the one people signed.
 */
const noteBarStyle: React.CSSProperties = {
  padding: '10px 14px',
  fontSize: TYPE.meta,
  lineHeight: 1.5,
  background: T.infoWeak,
  borderBottom: `1px solid ${T.info}`,
  color: T.text,
}

/** Something is waiting on the reader. Amber, above the actions. */
const approvalBoxStyle: React.CSSProperties = {
  marginTop: 16,
  padding: 14,
  borderRadius: RADIUS.md,
  border: `1px solid ${T.warning}`,
  background: T.warningWeak,
  color: T.warning,
}

/* Dashed and accent-coloured, never the state chip's fill: a suggestion is
   not a fact, and must not be able to pass for one at a glance. */
const aiBannerStyle: React.CSSProperties = {
  marginTop: 16,
  padding: 12,
  borderRadius: RADIUS.md,
  border: `1px dashed ${T.accent}`,
  background: T.accentWeak,
  fontSize: TYPE.small,
}

const errStyle: React.CSSProperties = {
  margin: '10px 0 0',
  fontSize: TYPE.meta,
  lineHeight: 1.5,
  color: T.error,
}

/* A rule that did not block. Amber, because green would say "all clear" about
   something the designer wrote precisely so it would be noticed, and red would
   say the save was refused when it was not. */
const advisoryStyle: React.CSSProperties = {
  margin: '10px 0 0',
  padding: '9px 12px',
  borderRadius: RADIUS.md,
  fontSize: TYPE.meta,
  lineHeight: 1.5,
  color: T.warning,
  background: T.warningWeak,
}

/* An outcome, not a warning and not a refusal: the action did what it was
   asked to do. Green, so it cannot be mistaken for either. */
const noticeStyle: React.CSSProperties = {
  margin: '10px 0 0',
  padding: '9px 12px',
  borderRadius: RADIUS.md,
  fontSize: TYPE.meta,
  lineHeight: 1.5,
  color: T.success,
  background: T.successWeak,
}

export default DocumentDetail

/**
 * Whether this document can be opened in the editor, which version that means,
 * and what the button should therefore SAY.
 *
 * One resolver rather than a condition at the button, because the answer has
 * three moving parts that have to agree: the right to edit, whether any
 * version carries an editable source at all, and where the document is in its
 * lifecycle. Split across two call sites they would drift, and the failure —
 * a button that opens the wrong version — is silent.
 */
function editorOpen(detail: DmsDocumentDetail, pickedVersionId: string): {
  versionId: string; label: string; hint?: string
} | null {
  // A version the editor can open: one that carries a source, or one whose
  // uploaded original can be imported into it. Uploading a Word or Pages SOP
  // used to leave it the one document in the repository nobody could revise —
  // the button simply was not there, with nothing saying why.
  const openable = detail.versions.filter((v) => v.is_editable || v.is_importable)
  const mode = detail.authoring?.mode || 'off'
  /*
   * `pickedVersionId` is what the reader DELIBERATELY chose in the history
   * list, not whichever version the page happens to be showing.
   *
   * The difference is the whole behaviour of this button. Defaulting to the
   * displayed version meant that on a document whose release is v1.0 and whose
   * latest work is v1.3, the page opened on v1.0 and the button offered to
   * edit v1.0 — so carrying on with the newest draft required first noticing
   * that the button named the wrong one. Untouched, this now always offers the
   * LATEST written version, which is what "edit this document" means. Click an
   * older version in the history and it follows you there, which is the only
   * time anybody means an older one.
   */
  const picked = pickedVersionId
    ? detail.versions.find((v) => String(v.id) === pickedVersionId)
    : undefined
  const canOpen = (v?: { is_editable?: boolean; is_importable?: boolean }) =>
    Boolean(v?.is_editable || v?.is_importable)
  const can = detail.rights.edit
    && (canOpen(picked) || (mode !== 'off' && openable.length > 0))
  if (!can) return null
  const selected = picked

  const revision = detail.revision
  const state = String(detail.document.lifecycle_state || 'draft')
  const inForce = state === 'approved' || state === 'published'

  /*
   * What "open in the editor" MEANS depends on where the document is.
   *
   * On a released document with a revision under way it means carry on with
   * the revision — opening the release instead would put the author back on
   * the approved text and file their work as a third version, orphaning what
   * they had already written. On a released document with nothing pending it
   * means start one, and saying so is the difference between an author
   * believing they are correcting the document in force and knowing they are
   * proposing a change to it.
   */
  if (revision?.in_progress && revision.candidate_is_editable) {
    return {
      versionId: revision.candidate_version_id,
      label: `Continue the revision (v${revision.candidate_label})`,
      hint: 'Opens the version that is waiting for approval, not the one in force.',
    }
  }

  const versionId = canOpen(selected)
    ? String(selected!.id)
    : String(openable[openable.length - 1].id)

  if (inForce) {
    return {
      versionId,
      label: 'Edit — start a revision',
      hint: 'The version in force stays in force. What you save becomes a new '
        + 'version that has to be approved before it replaces it.',
    }
  }
  return {
    versionId,
    label: canOpen(selected)
      ? `Edit v${selected!.version_label} in the editor`
      : 'Edit in the editor',
  }
}

/**
 * "Compare two versions".
 *
 * Offered ONLY for versions that carry an editable source, and the reason is
 * the one `is_editable` exists for: a version that was uploaded has no
 * structure to compare. Offering the control and letting the server refuse it
 * would teach the reader that the buttons here are unreliable.
 *
 * "Open in the editor" USED to live here too, and that was the bug: this sits
 * at the bottom of the version list, four sections down the side panel, so the
 * single most common thing anybody does to a draft — carry on writing it — was
 * the hardest control on the page to find. It is a primary action now, in the
 * actions row at the top where the lifecycle buttons are. Comparing two
 * versions stays here, next to the versions.
 */
function AuthoringActions({ documentId, detail, onAuthor }: {
  documentId: string
  detail: DmsDocumentDetail
  onAuthor: (mode:
    | { kind: 'open'; documentId: string; versionId?: string }
    | { kind: 'compare'; documentId: string; versionA: string; versionB: string }
  ) => void
}) {
  const editable = detail.versions.filter((v) => v.is_editable)
  const [a, setA] = useState('')
  const [b, setB] = useState('')

  // Two written versions is the minimum a comparison can mean anything with.
  if (editable.length < 2) return null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
      {editable.length >= 2 ? (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center',
                      flexWrap: 'wrap' }}>
          <select value={a} onChange={(e) => setA(e.target.value)}
                  style={{ ...selectStyle, flex: '1 1 90px' }}>
            <option value="">Earlier…</option>
            {editable.map((v) => (
              <option key={`a-${v.id}`} value={v.id}>v{v.version_label}</option>
            ))}
          </select>
          <select value={b} onChange={(e) => setB(e.target.value)}
                  style={{ ...selectStyle, flex: '1 1 90px' }}>
            <option value="">Later…</option>
            {editable.map((v) => (
              <option key={`b-${v.id}`} value={v.id}>v{v.version_label}</option>
            ))}
          </select>
          <button
            type="button"
            style={actionBtnStyle}
            disabled={!a || !b || a === b}
            onClick={() => onAuthor({ kind: 'compare', documentId,
                                      versionA: a, versionB: b })}
          >
            Compare
          </button>
        </div>
      ) : null}
    </div>
  )
}

const actionBtnStyle: React.CSSProperties = {
  ...sharedBtnStyle,
  justifyContent: 'flex-start',
  textAlign: 'left',
}

const selectStyle: React.CSSProperties = {
  padding: '5px 8px',
  borderRadius: 6,
  border: '1px solid var(--color-border, #ddd)',
  background: 'var(--color-bg, #fff)',
  color: 'inherit',
  fontSize: 13,
  minWidth: 0,
}
